import { useMutation, useQueryClient } from '@tanstack/react-query';

import { SizeSettings } from '@air/api';
import { callAirApi } from '@air/utils-api';
import { reportErrorToBugsnag } from '@air/utils-error';

import { getPrivateSizePresetsListKey } from '../usePrivateSizePresetsList';
import { getPrivateSizeSetsListKey } from '../usePrivateSizeSetsList';

export type UsePrivateSizePresetsDeleteMutationParams = NonNullable<Parameters<typeof SizeSettings.Presets.delete>[0]>;

export const usePrivateSizePresetsDeleteMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: UsePrivateSizePresetsDeleteMutationParams) => {
      return callAirApi<void>(() => SizeSettings.Presets.delete(params));
    },

    onError: (error, variables) => {
      reportErrorToBugsnag({
        context: 'Failed to delete size preset',
        error,
        metadata: {
          data: variables,
          key: 'data',
        },
      });
    },

    onSettled: async (_data, _error, variables) => {
      // Deleting a size cascades its membership rows away, so every set that referenced it just
      // shrank -- the sets list must refetch too, not only the size-presets list.
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: getPrivateSizePresetsListKey({ workspaceId: variables.workspaceId }),
        }),
        queryClient.invalidateQueries({
          queryKey: getPrivateSizeSetsListKey({ workspaceId: variables.workspaceId }),
        }),
      ]);
    },
  });
};
